/**
 * Curated TTS voices we expose in the UI. The backend's
 * `tts.ALLOWED_VOICES` mirror — keep them in lockstep, otherwise the
 * UI offers a voice the callable will reject.
 *
 * Cross-domain selection: en-GB (British, primary trial geography)
 * and en-US (covers expat / American family use cases). All Google
 * Neural2 voices — the more natural tier; ~25% more expensive than
 * Standard but trial-volume-acceptable.
 */

export interface VoiceOption {
  id: string;
  label: string;
}

export const CURATED_VOICES: VoiceOption[] = [
  { id: 'en-GB-Neural2-A', label: 'British female (warm)' },
  { id: 'en-GB-Neural2-B', label: 'British male (calm)' },
  { id: 'en-GB-Neural2-C', label: 'British female (clear)' },
  { id: 'en-US-Neural2-F', label: 'American female (gentle)' },
  { id: 'en-US-Neural2-D', label: 'American male (steady)' },
];

export const DEFAULT_VOICE = 'en-GB-Neural2-A';

// UI-band clamps, narrower than Google's hard limits. Server still
// validates against Google's limits in case of direct Firestore edits.
// Speaking rate caps at 1.0 — TTS faster than natural speech is
// counterproductive for an elderly listener; only slowing it makes
// sense.
// min/max/step are mirrored server-side in web/functions/audio_cache.py
// (RATE_MIN/MAX/QUANTUM, PITCH_MIN/MAX/QUANTUM) where they clamp+snap the
// audio cache key. KEEP IN LOCKSTEP: a drift makes the UI emit values off the
// server grid → near-miss cache misses, or collapses two slider positions.
export const SPEAKING_RATE = { min: 0.5, max: 1.0, step: 0.05, default: 0.75 };
export const PITCH = { min: -5, max: 5, step: 0.5, default: 0.0 };

// Wake/bedtime ranges mirror WAKE_HOUR_{MIN,MAX} and
// BEDTIME_HOUR_{MIN,MAX} in spoken_summary.py + the firestore rules.
export const WAKE_HOUR = { min: 4, max: 11, step: 1, default: 6 };
export const BEDTIME_HOUR = { min: 18, max: 23, step: 1, default: 21 };

export function formatHour(h: number): string {
  // 0..23 → "6am", "9pm" — friendlier than 24-hour for a UI label.
  const hh = ((h + 11) % 12) + 1;
  return `${hh}${h < 12 ? 'am' : 'pm'}`;
}
